import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';

import { ApplicationCycleContext } from '@/Contexts/ApplicationCycleContext';
import { ConfigContext } from '@/Contexts/ConfigContext';
import { DashboardContext } from '@/Contexts/DashboardContext';
import { LocaleContext } from '@/Contexts/LocaleContext';
import { SelectedPartnerContext } from '@/Contexts/SelectedPartnerContext';
import { SelectedProgramContext } from '@/Contexts/SelectedProgramContext';
import { UsersContext } from '@/Contexts/UsersContext';
import useApplication from '@/hooks/useApplication';
import usePartner from '@/hooks/usePartner';
import { ApplicationDataGridRowProps } from '@/models/Application';
import { ApplicationTypes, applicationTypeAsString } from '@/models/ApplicationType';
import { ApplicationCycle } from '@/models/ApplicationCycle';
import { PartnerYearsRecognized } from '@/models/PartnerYearsRecognized';
import { programIdToProgramName, ProgramNames, ProgramProps } from '@/models/Program';
import { ApplicationStatus } from '@/utils/constants';
import { logError } from '@/utils/errorHandlerUtil';
import { getStatusDescription } from '@/utils/statusUtil';

import { ApplicantDashboardRow } from '../ApplicantDashboardTypes';

interface UseApplicantDashboardDataResult {
  isDashboardReady: boolean;
  partnerApps: ApplicationDataGridRowProps[];
  dashboardRows: ApplicantDashboardRow[];
  selectedProgramInfo: ProgramProps;
}

function useApplicantDashboardData(
  currentPartnerId?: number | null
): UseApplicantDashboardDataResult | null {
  const { userCurrent } = useContext(UsersContext);
  const { partnerInfo, partnerId, updatePartnerId } = useContext(SelectedPartnerContext);
  const { partnerApps, updatePartnerApps } = useContext(DashboardContext);
  const { applicationCycles } = useContext(ApplicationCycleContext);
  const { localeLanguageCode } = useContext(LocaleContext);
  const { allStatuses } = useContext(ConfigContext);
  const { selectedProgramId, selectedProgramInfo, updateSelectedProgramId } =
    useContext(SelectedProgramContext);

  const { getPartner } = usePartner();
  const { getAppsForApplicantDashboard } = useApplication();

  const [dashboardRows, setDashboardRows] = useState<ApplicantDashboardRow[] | undefined>(
    undefined
  );

  const loadedPartnerIdRef = useRef<number | null>(null);

  const buildDashboardRow = useCallback(
    (
      app: ApplicationDataGridRowProps | undefined,
      cycleId: number,
      programId: ProgramNames,
      appTypeId: ApplicationTypes | null,
      id: string,
      appCycles: ApplicationCycle[]
    ): ApplicantDashboardRow => {
      const appCycle = appCycles.find((ac) => ac.id === cycleId);

      if (appCycle == null) {
        logError(`No application cycle found for cycleId ${cycleId}`);
        throw new Error(`No application cycle found for cycleId ${cycleId}`);
      }

      return {
        id,
        year: appCycle.year,
        applicationCycle: appCycle.applicationCycleName ?? '',
        statusDescription: getStatusDescription(
          allStatuses,
          app?.status ?? ApplicationStatus.ApplicationApproved,
          localeLanguageCode
        ),
        applicationTypeId: appTypeId,
        applicationType:
          appTypeId != null
            ? appTypeId === ApplicationTypes.Standard
              ? programIdToProgramName[programId]
              : applicationTypeAsString(appTypeId)
            : '',
        applicationId: app?.applicationId ?? null,
        submittedBy:
          app != null ? `${app.primaryContactFirstName} ${app.primaryContactLastName}` : '',
        status: app?.status ?? ApplicationStatus.ApplicationApproved,
      };
    },
    [allStatuses, localeLanguageCode]
  );

  const createDashboardRows = useCallback(
    (
      apps: ApplicationDataGridRowProps[],
      partnerYearsRecognized: PartnerYearsRecognized[],
      appCycles: ApplicationCycle[]
    ): ApplicantDashboardRow[] => {
      const appMap = new Map<string, ApplicationDataGridRowProps>();
      apps.forEach((app) => {
        appMap.set(`${app.applicationCycleId}-${app.applicationTypeId}`, app);
      });

      const processedAppIds = new Set<number>();

      const updatedAppDashRows: ApplicantDashboardRow[] = partnerYearsRecognized
        .sort((year1, year2) => year2.applicationCycleId - year1.applicationCycleId)
        .map((year: PartnerYearsRecognized) => {
          const app = appMap.get(`${year.applicationCycleId}-${year.applicationTypeId}`);

          if (app?.applicationId != null) {
            processedAppIds.add(app.applicationId);
          }

          return buildDashboardRow(
            app,
            year.applicationCycleId,
            year.programId,
            year.applicationTypeId,
            `${app?.applicationId}-${year.applicationCycleId}-${year.programId}-${year.applicationTypeId}`,
            appCycles
          );
        });

      const nonApprovedApps: ApplicantDashboardRow[] = apps
        .filter((app) => !processedAppIds.has(app.applicationId))
        .map((app: ApplicationDataGridRowProps) => {
          return buildDashboardRow(
            app,
            app.applicationCycleId,
            app.programId,
            app.applicationTypeId,
            `${app.applicationId}-${app.applicationCycleId}-${app.programId}-${app.applicationType}`,
            appCycles
          );
        });

      return [...updatedAppDashRows, ...nonApprovedApps].sort((a, b) => b.year - a.year);
    },
    [buildDashboardRow]
  );

  const processPartnerApps = useCallback(
    (
      result: ApplicationDataGridRowProps[],
      programId: ProgramNames
    ): ApplicationDataGridRowProps[] => {
      let apps: ApplicationDataGridRowProps[] = [];

      if (result.length > 0 && programId != null) {
        apps = result.filter((a: ApplicationDataGridRowProps) => a.applicationCycle != null);

        apps.sort(
          (a: ApplicationDataGridRowProps, b: ApplicationDataGridRowProps) =>
            Number(b.applicationCycle.split('/')[0]) - Number(a.applicationCycle.split('/')[0])
        );
      }

      return apps;
    },
    []
  );

  useEffect(() => {
    const loadPartnerDash = async (dashboardPartnerId: number) => {
      const currentPartner = await getPartner(dashboardPartnerId);

      if (
        currentPartner.id != null &&
        currentPartner.programId != null &&
        currentPartner.partnerYearsRecognized !== undefined
      ) {
        updatePartnerId(currentPartner.id);
        updateSelectedProgramId(currentPartner.programId);
        updatePartnerApps(undefined);

        const result = await getAppsForApplicantDashboard(currentPartner.id);
        const apps = processPartnerApps(result, currentPartner.programId);

        updatePartnerApps(apps);

        const updatedDashboardRows = createDashboardRows(
          apps,
          currentPartner.partnerYearsRecognized,
          applicationCycles
        );

        setDashboardRows(updatedDashboardRows);
        loadedPartnerIdRef.current = dashboardPartnerId;
      }
    };

    if (currentPartnerId != null && loadedPartnerIdRef.current !== currentPartnerId) {
      void loadPartnerDash(currentPartnerId);
    }
  }, [
    applicationCycles,
    createDashboardRows,
    currentPartnerId,
    getAppsForApplicantDashboard,
    getPartner,
    processPartnerApps,
    updatePartnerApps,
    updatePartnerId,
    updateSelectedProgramId,
  ]);

  const isDashboardReady = useMemo(
    () =>
      userCurrent != null &&
      selectedProgramId != null &&
      partnerId != null &&
      userCurrent.currentPartnerId === partnerInfo?.id &&
      userCurrent.currentPartnerId === partnerId &&
      partnerApps != null &&
      selectedProgramInfo != null &&
      selectedProgramInfo.id === selectedProgramId &&
      selectedProgramId === partnerInfo?.programId &&
      dashboardRows !== undefined,
    [
      dashboardRows,
      partnerApps,
      partnerId,
      partnerInfo?.id,
      partnerInfo?.programId,
      selectedProgramId,
      selectedProgramInfo,
      userCurrent,
    ]
  );

  if (
    !isDashboardReady ||
    selectedProgramInfo == null ||
    partnerApps == null ||
    dashboardRows == null
  ) {
    return null;
  }

  return {
    isDashboardReady,
    partnerApps,
    dashboardRows,
    selectedProgramInfo,
  };
}

export default useApplicantDashboardData;
